// Exam countdown engine — exact port of v3 CountDownTimer's time computation
// (src/pages/experiences/show/take/commonView/CountDownTimer.tsx; anchors in
// workspaces/APL-11850/design/exam-header-notes.md §3). Replaces react-countdown
// with a 1s interval on Date.now(). Seconds are ROUNDED to match react-countdown's
// calcTimeDelta (precision 0): display, warnings and completion all share v3's
// half-second boundary. All user_info writes are typed callbacks — the engine
// never touches a store.
import { useEffect, useRef, useState } from 'react';

import { hasPauseStatus } from '@/pages/experiences/utils/realtimeExperienceHelper';

import type { ExamCountdownConfig, ExamCountdownState } from './types';

/** v3 convertMs (commonHelpers.ts:1247) — minutes → ms; local to keep the engine dependency-light. */
export const examMinutesToMs = (minutes: number): number => Number(minutes) * 60 * 1000;

/** v3 react-countdown daysInHours render — HH:MM:SS, hours unbounded, seconds rounded (calcTimeDelta precision 0). */
export const formatExamClock = (remainingMs: number): string => {
  const totalSeconds = Math.max(0, Math.round(remainingMs / 1000));
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(Math.floor(totalSeconds / 3600))}:${pad(
    Math.floor((totalSeconds % 3600) / 60)
  )}:${pad(totalSeconds % 60)}`;
};

/** v3 mount-path settle delay before the expiry write (CountDownTimer.tsx:201-216). */
const MOUNT_EXPIRE_DELAY_MS = 1000;

/** v3 getPausedTime (:97-102) — the still-running part of a pause; 0 before start. */
export const pausedTimeMs = ({
  startedAt,
  studentPauseStatus = null,
  studentPausedAt,
  experiencePaused = false,
  experiencePausedAt,
  now,
}: {
  startedAt?: number | null;
  studentPauseStatus?: string | null;
  studentPausedAt?: number | null;
  experiencePaused?: boolean;
  experiencePausedAt?: number | null;
  now: number;
}): number => {
  if (!startedAt) return 0;
  // Student side treats paused AND unpause_pending as paused (v3 :94-96).
  const studentPaused =
    hasPauseStatus(studentPauseStatus as any, 'paused') ||
    hasPauseStatus(studentPauseStatus as any, 'unpause_pending');
  if (studentPaused && studentPausedAt) return now - studentPausedAt;
  if (experiencePaused && experiencePausedAt) return now - experiencePausedAt;
  return 0;
};
const WARN_10_SECONDS = 10 * 60;
const WARN_2_SECONDS = 2 * 60;

export const useExamCountdown = (config: ExamCountdownConfig): ExamCountdownState => {
  const {
    startedAt,
    duration,
    durationIsMs = false,
    extraTime = 0,
    extraTimeOffset = 0,
    unpausedOffset = 0,
    pauseEnabled = false,
    studentPauseStatus = null,
    studentPausedAt,
    experiencePaused = false,
    experiencePausedAt,
    isTimeUp = false,
  } = config;

  // Student side treats paused AND unpause_pending as paused (v3 :94-96).
  const isStudentPaused =
    hasPauseStatus(studentPauseStatus, 'paused') ||
    hasPauseStatus(studentPauseStatus, 'unpause_pending');
  const isPaused = isStudentPaused || experiencePaused;

  const durationMs = durationIsMs ? duration : examMinutesToMs(duration);

  const getPausedTime = () =>
    pausedTimeMs({
      startedAt,
      studentPauseStatus,
      studentPausedAt,
      experiencePaused,
      experiencePausedAt,
      now: Date.now(),
    });
  const calcPausedOffset = () => (pauseEnabled ? getPausedTime() + (unpausedOffset || 0) : 0);
  // v3 calcTargetTime (:110-115) — studentStartedAt falls back to Date.now() per computation.
  const computeTargetTime = (pausedOffsetMs: number) =>
    (startedAt ?? Date.now()) + durationMs + extraTime + extraTimeOffset + pausedOffsetMs;

  const [pausedOffset, setPausedOffset] = useState(calcPausedOffset);
  const [targetTime, setTargetTime] = useState(() => computeTargetTime(calcPausedOffset()));
  const [remainingMs, setRemainingMs] = useState(() => Math.max(0, targetTime - Date.now()));
  const [isExpired, setIsExpired] = useState(false);

  // Latest-callback refs — the tick interval must never capture stale closures.
  const callbacksRef = useRef(config);
  callbacksRef.current = config;

  // v3's LOCAL isTimeUp latch (:118): set by the store mirror (both directions),
  // by either expiry path, and CLEARED synchronously by the extra-time revive —
  // the tick-expiry guard reads THIS, never the (async) store prop, so a slow
  // is_time_up:false round-trip can't swallow a legitimate second expiry.
  const timeUpLatchRef = useRef(false);
  const warned10Ref = useRef(false);
  const warned2Ref = useRef(false);
  const prevSecondsRef = useRef<number | null>(null);

  // Effects below preserve v3's declaration order: extra-time (:121) → pause
  // (:165) → store-mirror sync (:182) → mount-check (:185) → tick loop.

  // v3 extra-time effect (:121-142): rebuild target; > 1s remaining revives an
  // expired test (host clears is_time_up via onTimeRestored — fires on mount
  // too, v3 behavior). v3's `typeof extra_time != 'undefined'` gate is
  // always-true after destructuring defaults, so recompute unconditionally.
  useEffect(() => {
    const nextTarget = computeTargetTime(pausedOffset);
    setTargetTime(nextTarget);
    if (Math.trunc((nextTarget - Date.now()) / 1000) > 1) {
      callbacksRef.current.onTimeRestored?.();
      setIsExpired(false);
      timeUpLatchRef.current = false;
      // v3 re-keys the countdown on targetTime — completion/warning ticks re-arm.
      warned10Ref.current = false;
      warned2Ref.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraTime, pausedOffset]);

  // v3 pause effect (:165-178): recompute pausedOffset on pause-state flips.
  // The tick effect below freezes/resumes off `isPaused` (v3 countDownRef.pause/start).
  useEffect(() => {
    if (!pauseEnabled) return;
    setPausedOffset(calcPausedOffset());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStudentPaused, experiencePaused]);

  // v3 :182-184 — store mirror wins, both directions.
  useEffect(() => {
    timeUpLatchRef.current = isTimeUp;
    setIsExpired(isTimeUp);
  }, [isTimeUp]);

  // v3 mount-check (:185-224): already past target on mount → expired, write
  // deferred 1s. v3 fires it regardless of the store's is_time_up. (v3 ALSO
  // double-fired here — react-countdown emits onComplete immediately for a
  // past date — duplicating the write + log; not ported, single 'mount' fire.)
  useEffect(() => {
    callbacksRef.current.onMount?.();
    if (targetTime < Date.now()) {
      setIsExpired(true);
      timeUpLatchRef.current = true;
      const timeout = setTimeout(
        () => callbacksRef.current.onExpire?.({ reason: 'mount' }),
        MOUNT_EXPIRE_DELAY_MS
      );
      return () => clearTimeout(timeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The tick loop — 1s cadence off Date.now(), frozen while paused. Re-arms on
  // targetTime change (v3 re-key) with prev seeded from the current remaining,
  // so a first tick that lands ON a threshold still registers as a crossing.
  useEffect(() => {
    const initialRemaining = Math.max(0, targetTime - Date.now());
    setRemainingMs(initialRemaining);
    prevSecondsRef.current = Math.round(initialRemaining / 1000);
    if (isPaused) return;

    const interval = setInterval(() => {
      const remaining = Math.max(0, targetTime - Date.now());
      setRemainingMs(remaining);
      const seconds = Math.round(remaining / 1000);
      const prev = prevSecondsRef.current;
      prevSecondsRef.current = seconds;

      // v3 completion = rounded total <= 0 (react-countdown fires up to 499ms
      // before wall-clock zero); warnings are suppressed on the completing
      // tick (v3 skips onTick when completed). Guarded on the local latch —
      // exactly v3's onComplete `!isTimeUp` check (:368).
      if (seconds <= 0) {
        clearInterval(interval);
        if (!timeUpLatchRef.current) {
          timeUpLatchRef.current = true;
          setIsExpired(true);
          callbacksRef.current.onExpire?.({ reason: 'tick' });
        }
        return;
      }

      // v3 warns on the EXACT 0h10m0s / 0h2m0s rounded tick (:226-266); with
      // continuous 1s ticks the crossing fires on the SAME tick v3's equality
      // does (prev seeding above), and it's a strict superset under suspended-
      // tab skips — still fires once (latched), re-armed by the revive reset.
      if (prev !== null) {
        if (!warned10Ref.current && prev > WARN_10_SECONDS && seconds <= WARN_10_SECONDS) {
          warned10Ref.current = true;
          callbacksRef.current.onWarning?.({ minutesLeft: 10 });
        }
        if (!warned2Ref.current && prev > WARN_2_SECONDS && seconds <= WARN_2_SECONDS) {
          warned2Ref.current = true;
          callbacksRef.current.onWarning?.({ minutesLeft: 2 });
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [targetTime, isPaused]);

  return {
    remainingMs,
    clock: formatExamClock(remainingMs),
    isPaused,
    isExpired,
    targetTime,
  };
};
